import { defineMessages, MessageDescriptor, type IntlShape } from "react-intl";

import { convertToFuzzyTimeFormat } from "@/shared/utils/common.utils";

import { ICandidateAssessment } from "@/modules/assessments/assessments.types";
import { ILocalizedTestElement, ITestElement } from "@/modules/assessments/types/test.interface";

import { ITextLocation } from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import { TextLocations } from "@/modules/assessments/scenes/CreateAssessment/createAssessment.constants";
import { QuestionTypes } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { ITestAnswer } from "@/shared/components/charts/types/chartTypes";
import { getNextQuestionPage } from "../assessment/CandidateAssessment/utils/candidate-assessment-view.utils";
import { pickAssessmentCopyVariant, type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";

export function getQuestionNumberByCurrentPage(currentPage: number, questionsList: ILocalizedTestElement[][]): number {
  let counter: number = 0;
  const questionFirstIndexOnPageList: number[] = questionsList.map((pageQuestionsList) => {
    const firstIndex = counter;
    counter = pageQuestionsList.length + counter;
    return firstIndex;
  });
  return questionFirstIndexOnPageList[currentPage - 1] + 1;
}

export function getCurrentTextNumber(maxTestIndexInModule: number[], currentQuestion: number) {
  const i = maxTestIndexInModule.findIndex((element) => element >= currentQuestion);
  return i === -1 ? maxTestIndexInModule.length : i + 1;
}

// TODO: Refactor this approach, many components depend on this function; however, they only use some of the values
export function getTestsProps({
  assessment,
  textLocations,
  intl,
}: {
  assessment: ICandidateAssessment | undefined;
  textLocations: ITextLocation[];
  intl?: IntlShape;
}) {
  if (!assessment) {
    return {
      imageUrl: "",
      title: "",
      leftTime: "",
      introText: "",
      landingText: "",
      outroText: "",
    };
  }

  const introTextId = textLocations.find((textLocation) => textLocation.name === TextLocations.introText)?.locationId;
  const outroTextId = textLocations.find((textLocation) => textLocation.name === TextLocations.outroText)?.locationId;
  const landingTextId = textLocations.find(
    (textLocation) => textLocation.name === TextLocations.landingText,
  )?.locationId;

  const { assessmentTests, assessmentTexts, name, titleImageUrl: imageUrl } = assessment;
  const title = name.value ?? "";

  const maxTestIndexInModule: number[] = getMaxTestIndexInModule(getAllTests(assessment));

  let leftTime: string = convertToFuzzyTimeFormat({ milliseconds: 0, intl });

  if (assessmentTests && assessmentTests.length > 0) {
    leftTime = convertToFuzzyTimeFormat({
      milliseconds: assessmentTests
        .map((assessmentTest) => assessmentTest.test.config.suggestedTime || 0)
        .reduce((a, b) => a + b),
      intl,
    });
  }

  const readText = (locationId?: number): string =>
    assessmentTexts?.find((text) => text.locationId === locationId)?.body?.value ?? "";

  const introText = readText(introTextId);
  const outroText = readText(outroTextId);
  const landingText = readText(landingTextId);

  return {
    imageUrl,
    title,
    leftTime,
    introText,
    landingText,
    outroText,
    maxTestIndexInModule,
  };
}

function getAllTests(assessment: ICandidateAssessment): ITestElement[][] {
  if (assessment && assessment.assessmentTests) {
    return assessment.assessmentTests
      .sort((a, b) => a.rank - b.rank)
      .map(({ test: { testElements } }) => {
        return testElements.sort((a, b) => a.rank - b.rank);
      });
  }
  return [];
}

function getMaxTestIndexInModule(allTests: ITestElement[][]): number[] {
  let quantity: number = 0;
  return allTests.map((item) => {
    quantity += item.length;
    return quantity;
  });
}

const navMessages = defineMessages({
  next: { id: "candidate.taking.nav.next", defaultMessage: "next" },
  finishAssessment: { id: "candidate.taking.assessment.nav.finish", defaultMessage: "finish assessment" },
  finishSurvey: { id: "candidate.taking.survey.nav.finish", defaultMessage: "finish survey" },
  startSection: { id: "candidate.taking.nav.startSection", defaultMessage: "start section" },
  nextSection: { id: "candidate.taking.nav.nextSection", defaultMessage: "next section" },
});

const finishMessages: AssessmentCopyVariants = {
  assessment: navMessages.finishAssessment,
  survey: navMessages.finishSurvey,
};

/**
 * @param {number} currentPage - the 1-based page the candidate is on
 * @param {ILocalizedTestElement[][]} questionsList - every page of questions in the attempt
 * @param {ITestAnswer[]} answers - answers saved so far
 * @param {boolean} isSurvey - whether the assessment is flagged as an org survey
 * @returns {MessageDescriptor} the message for the forward navigation button
 */
export const getNextButtonText = ({
  currentPage,
  questionsList,
  answers,
  isSurvey,
}: {
  currentPage: number;
  questionsList: ILocalizedTestElement[][];
  answers: ITestAnswer[];
  isSurvey: boolean;
}): MessageDescriptor => {
  if (currentPage < 1 || currentPage > questionsList.length) return navMessages.next;

  const currentPageQuestions = questionsList[currentPage - 1];
  const hasNextPage = currentPage < questionsList.length;
  const nextPageQuestions = hasNextPage ? questionsList[currentPage] : [];

  const isLastPage = !hasNextPage;
  const isIntroPageOfSection =
    currentPageQuestions.length === 1 && currentPageQuestions[0]?.config.type === QuestionTypes.Text;
  const isFinalPageOfSection =
    hasNextPage && nextPageQuestions.length === 1 && nextPageQuestions[0]?.config.type === QuestionTypes.Text;

  const nextQuestionPage = getNextQuestionPage(currentPage, questionsList, answers);

  if (isLastPage || nextQuestionPage > questionsList.length) {
    return pickAssessmentCopyVariant({ variants: finishMessages, isSurvey });
  } else if (isIntroPageOfSection) {
    return navMessages.startSection;
  } else if (isFinalPageOfSection) {
    return navMessages.nextSection;
  } else {
    return navMessages.next;
  }
};
